import * as React from 'react';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const toggleVariants = cva(
  `
    inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium
    transition-colors

    [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0

    data-[state=on]:bg-accent data-[state=on]:text-accent-foreground

    disabled:pointer-events-none disabled:opacity-50

    focus-visible:ring-1 focus-visible:outline-none focus-visible:ring-ring

    hover:bg-muted hover:text-muted-foreground
  `,
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: `
          border border-input bg-transparent shadow-sm

          hover:bg-accent hover:text-accent-foreground
        `,
      },
      size: {
        default: 'h-9 min-w-9 px-2',
        sm: 'h-8 min-w-8 px-1.5',
        lg: 'h-10 min-w-10 px-2.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

// eslint-disable-next-line react-refresh/only-export-components
export { Toggle, toggleVariants };
